/**
 * The `consentDetails` value tracking carries next to `consentStatus`: the
 * recorded {@link ConsentDecision} in one fixed-width string, agreed across
 * all TV 2 platforms (web, Smart TV, apps) so downstream reporting can split
 * it with a single rule:
 *
 *   (onetrust|cookiebot)|statistics:(0|1),preferences:(0|1),marketing:(0|1),
 *                        iab2-p1:(0|1),iab2-stack1:(0|1),iab2-stack42:(0|1)
 *
 * Every category is always present, in this order, as `0`/`1`. CMPs that do
 * not expose a category (Cookiebot has no IAB groups) report it as `0`.
 *
 * `consentStatus` is derived from the very same decision, see
 * {@link deriveConsentTrackingStatus}, so the two fields can never disagree.
 */
import {
  CONSENT_CATEGORIES,
  type ConsentCategory,
  type ConsentDecision,
  type ConsentTrackingStatus,
} from './consentManager';

export type ConsentCmp = 'onetrust' | 'cookiebot';

/** The wire label of each category. */
const CONSENT_DETAIL_LABELS: Record<ConsentCategory, string> = {
  Statistics: 'statistics',
  Preferences: 'preferences',
  Marketing: 'marketing',
  IabPurpose1: 'iab2-p1',
  IabStack1: 'iab2-stack1',
  IabStack42: 'iab2-stack42',
};

/** The categories OneTrust exposes - all of them, the TCF groups included. */
export const ONETRUST_EXPOSED_CATEGORIES: ReadonlyArray<ConsentCategory> = CONSENT_CATEGORIES;

/** The categories Cookiebot exposes - no TCF groups, so those stay `0` and are not judged. */
export const COOKIEBOT_EXPOSED_CATEGORIES: ReadonlyArray<ConsentCategory> = [
  'Statistics',
  'Preferences',
  'Marketing',
];

export function formatConsentDetails(cmp: ConsentCmp, decision: ConsentDecision): string {
  const fields = CONSENT_CATEGORIES.map(
    category => `${CONSENT_DETAIL_LABELS[category]}:${decision[category] ? '1' : '0'}`
  ).join(',');
  return `${cmp}|${fields}`;
}

/**
 * The one-word `consentStatus` a decision amounts to. Judged on the same
 * categories, with the same counting, as `consentDetails`: every category the
 * CMP exposes must be granted for `accepted`; any declined among them is
 * `declined`. A `null` decision means the user has not confirmed a choice yet.
 */
export function deriveConsentTrackingStatus(
  decision: ConsentDecision | null,
  exposed: ReadonlyArray<ConsentCategory>
): ConsentTrackingStatus {
  if (decision === null) return 'notDecided';
  return exposed.every(category => decision[category]) ? 'accepted' : 'declined';
}
